import { useState, useEffect, useCallback } from 'react';

const API = 'https://tcio-api.onrender.com';

const s = {
  container: { padding: '32px', maxWidth: '1000px', margin: '0 auto' },
  title: { margin: '0 0 4px', fontSize: '24px', color: '#336090' },
  subtitle: { margin: '0 0 20px', color: '#666', fontSize: '14px' },
  tabRow: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
  tab: { padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' },
  tabOn: { padding: '8px 16px', borderRadius: 8, border: '1px solid #336090', background: '#336090', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'white' },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  td: { padding: '12px 14px', borderBottom: '1px solid #f0f4f8', fontSize: '14px', color: '#1a2a3a', verticalAlign: 'middle' },
  pill: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700' },
  btnSm: { padding: '5px 11px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #d1d5db', background: 'white', color: '#374151', marginRight: '6px' },
  btnDanger: { padding: '5px 11px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #fca5a5', background: '#fef2f2', color: '#991b1b' },
  btnOn: { padding: '5px 11px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#166534' },
  input: { padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '14px', boxSizing: 'border-box' },
  addBtn: { background: '#336090', color: 'white', border: 'none', padding: '9px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  addRow: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' },
};

const LIST_DEFS = [
  { key: 'therapists',    perm: 'staff',             label: 'Therapists' },
  { key: 'case_managers', perm: 'staff',             label: 'Case Managers' },
  { key: 'partners',      perm: 'referral_partners', label: 'Referral Partners' },
  { key: 'sources',       perm: 'lead_sources',      label: 'Lead Sources' },
  { key: 'facilities',    perm: null,                label: 'Facilities' }, // ceo/admin only
];

export default function Lists({ user }) {
  const isAdmin = ['ceo','admin'].includes(user.role);
  const perms = user.list_permissions || {};
  const canSee  = (p) => isAdmin || (p && perms[p]);
  const canEdit = (p) => isAdmin || (p && perms[p] === 'edit');

  const visible = LIST_DEFS.filter(d => d.perm === null ? isAdmin : canSee(d.perm));
  const [tab, setTab] = useState(visible[0]?.key);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [ef, setEf] = useState({});
  const [nf, setNf] = useState({});
  const [showAdd, setShowAdd] = useState(false);

  const headers = { Authorization: `Bearer ${user.token}` };
  const jh = { ...headers, 'Content-Type': 'application/json' };

  const def = LIST_DEFS.find(d => d.key === tab) || {};
  const editable = def.perm === null ? isAdmin : canEdit(def.perm);

  const endpoints = {
    therapists:    { list: '/api/staff', create: '/api/staff', patch: (id) => `/api/staff/${id}` },
    case_managers: { list: '/api/staff', create: '/api/staff', patch: (id) => `/api/staff/${id}` },
    partners:      { list: '/api/admin/referral-partners', create: '/api/referral-partners', patch: (id) => `/api/referral-partners/${id}` },
    sources:       { list: '/api/admin/lead-sources', create: '/api/lead-sources', patch: (id) => `/api/lead-sources/${id}` },
    facilities:    { list: '/api/admin/facilities', create: null, patch: (id) => `/api/admin/facilities/${id}` },
  };

  const load = useCallback(async () => {
    if (!tab) return;
    const r = await fetch(`${API}${endpoints[tab].list}`, { headers });
    const j = await r.json();
    if (!j.ok) { setRows([]); return; }
    let data = j.data;
    if (tab === 'therapists') data = data.filter(x => x.staff_type === 'therapist');
    if (tab === 'case_managers') data = data.filter(x => x.staff_type === 'case_manager');
    setRows(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(() => { load(); setEditId(null); setShowAdd(false); setNf({}); }, [load]);

  const patch = async (id, body) => {
    setBusy(true);
    await fetch(`${API}${endpoints[tab].patch(id)}`, { method: 'PATCH', headers: jh, body: JSON.stringify(body) });
    setBusy(false); setEditId(null); load();
  };
  const create = async () => {
    setBusy(true);
    let body = { ...nf };
    if (tab === 'therapists') body.staff_type = 'therapist';
    if (tab === 'case_managers') body.staff_type = 'case_manager';
    await fetch(`${API}${endpoints[tab].create}`, { method: 'POST', headers: jh, body: JSON.stringify(body) });
    setBusy(false); setNf({}); setShowAdd(false); load();
  };

  const nameOf = (r) =>
    tab === 'partners' ? r.name
    : tab === 'sources' ? r.name
    : tab === 'facilities' ? r.org_name
    : `${r.first_name || ''} ${r.last_name || ''}`.trim();

  const isStaff = tab === 'therapists' || tab === 'case_managers';

  return (
    <div style={s.container}>
      <h1 style={s.title}>Staff & Lists</h1>
      <p style={s.subtitle}>Changes update every dropdown instantly. Deactivate — never delete — so history stays intact.</p>

      <div style={s.tabRow}>
        {visible.map(d => (
          <button key={d.key} style={tab === d.key ? s.tabOn : s.tab} onClick={() => setTab(d.key)}>{d.label}</button>
        ))}
      </div>

      {endpoints[tab]?.create && (
        <div style={{ marginBottom: 12 }}>
          <button style={s.addBtn} onClick={() => setShowAdd(!showAdd)}>{showAdd ? 'Close' : `+ Add ${def.label?.replace(/s$/, '')}`}</button>
        </div>
      )}

      {showAdd && (
        <div style={s.addRow}>
          {isStaff ? (
            <>
              <input style={{ ...s.input, width: 150 }} placeholder="First name" value={nf.first_name || ''} onChange={e => setNf({ ...nf, first_name: e.target.value })} />
              <input style={{ ...s.input, width: 150 }} placeholder="Last name" value={nf.last_name || ''} onChange={e => setNf({ ...nf, last_name: e.target.value })} />
            </>
          ) : tab === 'partners' ? (
            <>
              <input style={{ ...s.input, width: 180 }} placeholder="Partner name" value={nf.name || ''} onChange={e => setNf({ ...nf, name: e.target.value })} />
              <input style={{ ...s.input, width: 150 }} placeholder="Contact name" value={nf.contact_name || ''} onChange={e => setNf({ ...nf, contact_name: e.target.value })} />
              <input style={{ ...s.input, width: 130 }} placeholder="Phone" value={nf.phone || ''} onChange={e => setNf({ ...nf, phone: e.target.value })} />
              <input style={{ ...s.input, width: 170 }} placeholder="Email" value={nf.email || ''} onChange={e => setNf({ ...nf, email: e.target.value })} />
            </>
          ) : (
            <input style={{ ...s.input, width: 200 }} placeholder="Source name" value={nf.name || ''} onChange={e => setNf({ ...nf, name: e.target.value })} />
          )}
          <button disabled={busy || !(isStaff ? (nf.first_name || '').trim() : (nf.name || '').trim())} style={s.addBtn} onClick={create}>Add</button>
        </div>
      )}

      <table style={s.table}>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} style={{ opacity: r.active ? 1 : 0.5 }}>
              <td style={s.td}>
                {editId === r.id ? (
                  isStaff ? (
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <input style={{ ...s.input, width: 130 }} value={ef.first_name || ''} onChange={e => setEf({ ...ef, first_name: e.target.value })} placeholder="First" />
                      <input style={{ ...s.input, width: 130 }} value={ef.last_name || ''} onChange={e => setEf({ ...ef, last_name: e.target.value })} placeholder="Last" />
                    </span>
                  ) : (
                    <input style={{ ...s.input, width: 220 }} value={ef.name || ''} onChange={e => setEf({ ...ef, name: e.target.value })} />
                  )
                ) : (
                  <span>
                    {nameOf(r) || <em style={{ color: '#b45309' }}>(no name)</em>}
                    {tab === 'partners' && r.contact_name ? <span style={{ color: '#666', fontSize: 12 }}> · {r.contact_name}{r.phone ? ` · ${r.phone}` : ''}</span> : null}
                  </span>
                )}
              </td>
              <td style={s.td}>
                <span style={{ ...s.pill, background: r.active ? '#dcfce7' : '#f3f4f6', color: r.active ? '#166534' : '#6b7280' }}>
                  {r.active ? 'ACTIVE' : 'INACTIVE'}
                </span>
                {tab === 'facilities' && editable && (
                  <select disabled={busy} value={r.person_term || 'client'} title="How this facility refers to the people it serves - drives form titles and wording"
                    onChange={e => patch(r.id, { person_term: e.target.value })}
                    style={{ marginLeft: 8, font: 'inherit', fontSize: 12, padding: '3px 6px', borderRadius: 6, border: '1px solid #d1d5db' }}>
                    {[['client', 'Client'], ['patient', 'Patient'], ['resident', 'Resident'], ['participant', 'Participant']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                )}
              </td>
              <td style={{ ...s.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                {editable && (editId === r.id ? (
                  <>
                    <button disabled={busy} style={s.btnSm} onClick={() => patch(r.id,
                      isStaff ? { first_name: ef.first_name, last_name: ef.last_name }
                      : tab === 'facilities' ? { org_name: ef.name }
                      : { name: ef.name })}>Save</button>
                    <button style={s.btnSm} onClick={() => setEditId(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button style={s.btnSm} onClick={() => { setEditId(r.id); setEf(isStaff ? { first_name: r.first_name || '', last_name: r.last_name || '' } : { name: nameOf(r) }); }}>Edit</button>
                    <button disabled={busy} style={r.active ? s.btnDanger : s.btnOn} onClick={() => patch(r.id, { active: !r.active })}>
                      {r.active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
